import { useContext } from 'react';
import { Button } from 'react-bootstrap';
import { useFormContext } from 'react-hook-form';
import t from 'react-translate';
import { copyToClipboard } from 'shared/react-utils';
import { AngularContext } from 'react-app';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { setSaveStatus } from 'redux/actions/mentoring-programs';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { updateMentoringProgramRecruitment } from 'redux/actions/mentoring-program-recruitments';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import NvAthenaFroala from 'athena/components/nv-athena-froala';
import NvCheckbox from 'shared/components/inputs/nv-checkbox';
import NvIcon from 'shared/components/nv-icon';
import ParticipantPill from 'athena/components/participant-pill';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { config } from '@config/pendo.config.json';
import { ReactComponent as WhoThisIsForIcon } from 'styles/icons/customer-success.svg';
import { ReactComponent as WhatToExpectIcon } from 'styles/icons/employee-onboarding.svg';
import { ReactComponent as WhyJoinIcon } from 'styles/icons/training-providers.svg';
import PageContents from './page-contents';


type RegistrationPageProps = {
  role: MentoringProgramRole;
  namePrefix: 'mentor' | 'mentee';
  // When both roles are recruited, headers/buttons are role-specific and the shared
  // Page Contents block is rendered once by the parent instead of inside each section.
  isBoth?: boolean;
};

const flyerRoleByProgramRole: Partial<Record<MentoringProgramRole, string>> = {
  [MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR]: 'mentor',
  [MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE]: 'mentee',
};

const RegistrationPage = ({ role, namePrefix, isBoth }: RegistrationPageProps) => {
  const p = (name: string) => `${namePrefix}${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  const dispatch = useAppDispatch();
  const { getValues } = useFormContext();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: programId,
    recruitments,
    programAlias,
  } = mentoringProgram || {};
  const recruitment = recruitments?.[flyerRoleByProgramRole[role]];
  const recruitmentId = recruitment?.id;

  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);

  // RHF updates its value after a checkbox's onChange and on a Froala blur, so we defer the
  // read to the next tick to capture the latest values before persisting.
  const saveRecruitment = () => {
    if (!recruitmentId) return;
    setTimeout(() => {
      dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
      wrapThunkAction(
        dispatch(
          updateMentoringProgramRecruitment({
            programId,
            role: namePrefix,
            recruitment: {
              id: recruitmentId,
              requirements: getValues(p('requirements')),
              includeRequirements: getValues(p('includeRequirements')),
              expectations: getValues(p('expectations')),
              includeExpectations: getValues(p('includeExpectations')),
              benefits: getValues(p('benefits')),
              includeBenefits: getValues(p('includeBenefits')),
            },
          }),
        ),
      ).then(() => {
        dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
      }).finally(() => {
        setTimeout(() => {
          dispatch(setSaveStatus({ newStatus: null }));
        }, 3000);
      });
    });
  };

  const copyLink = () => {
    const registrationUrl = $state.href(
      'mentoring-program-flyer',
      { programId, recruitmentId },
      { absolute: true },
    );
    copyToClipboard(registrationUrl);
  };

  const dataQABase = role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE
    ? config.pendo.athena.mentorshipProgram.settings.recruitment.mentee
    : config.pendo.athena.mentorshipProgram.settings.recruitment.mentor;

  const roleAlias = (role === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE
    ? programAlias?.mentee?.capitalizedSingularized
    : programAlias?.mentor?.capitalizedSingularized) ?? '';

  const title = isBoth
    ? t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.TITLE_WITH_ROLE({ roleAlias })
    : t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.TITLE();
  const copyLabel = isBoth
    ? t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.COPY_LINK_WITH_ROLE({ roleAlias })
    : t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.COPY_LINK();

  return (
    <>
      {/* Card header */}
      <div className='d-flex align-items-center justify-content-between pb-4 border-bottom border-gray-5'>
        <div className='d-flex align-items-center'>
          <div className='heading-5 mr-2'>{title}</div>
          <ParticipantPill role={role} />
        </div>
        <Button
          variant='secondary'
          className='d-flex align-items-center'
          onClick={copyLink}
          data-qa={dataQABase.copyLink}
        >
          <NvIcon icon='link' size='smallest' className='mr-1' />
          {copyLabel}
        </Button>
      </div>

      <div className='mt-4'>
        {!isBoth && <PageContents />}

        {/* Customizable RTE sections */}
        {/* Who this is for */}
        <div className='mb-4'>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <div className='d-flex align-items-center'>
              <WhoThisIsForIcon width={20} height={20} className='mr-2' />
              <div className='heading-5'>
                {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.WHO_THIS_IS_FOR()}
              </div>
            </div>
            <NvCheckbox
              withForm
              name={p('includeRequirements')}
              label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.INCLUDE()}
              onChange={saveRecruitment}
              dataQa={dataQABase.includeWhoIsThisFor}
            />
          </div>
          <NvAthenaFroala
            name={p('requirements')}
            className='w-100'
            scrollableContainer='body'
            onBlur={saveRecruitment}
            dataQa={dataQABase.whoIsThisForInputField}
          />
        </div>

        {/* What to expect */}
        <div className='mb-4'>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <div className='d-flex align-items-center'>
              <WhatToExpectIcon width={20} height={20} className='mr-2' />
              <div className='heading-5'>
                {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.WHAT_TO_EXPECT()}
              </div>
            </div>
            <NvCheckbox
              withForm
              name={p('includeExpectations')}
              label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.INCLUDE()}
              onChange={saveRecruitment}
              dataQa={dataQABase.includeWhatToExpect}
            />
          </div>
          <NvAthenaFroala
            name={p('expectations')}
            className='w-100'
            scrollableContainer='body'
            onBlur={saveRecruitment}
            dataQa={dataQABase.whatToExpectInputField}
          />
        </div>

        {/* Why join? */}
        <div>
          <div className='d-flex align-items-center justify-content-between mb-2'>
            <div className='d-flex align-items-center'>
              <WhyJoinIcon width={20} height={20} className='mr-2' />
              <div className='heading-5'>
                {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.WHY_JOIN()}
              </div>
            </div>
            <NvCheckbox
              withForm
              name={p('includeBenefits')}
              label={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.INCLUDE()}
              onChange={saveRecruitment}
              dataQa={dataQABase.includeWhyJoin}
            />
          </div>
          <NvAthenaFroala
            name={p('benefits')}
            className='w-100'
            scrollableContainer='body'
            onBlur={saveRecruitment}
            dataQa={dataQABase.whyJoinInputField}
          />
        </div>
      </div>
    </>
  );
};

export default RegistrationPage;
